import React, { useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Spinner, Card, Select, TextInput, Label, Button } from 'flowbite-react';
import * as wasm from 'sched';
import { useDatabase } from '../../hooks/useDatabase';
import { useTeam } from '../../hooks/useTeam';
import EditableTitle from '../../components/EditableTitle';
import Avatar from '../../components/common/Avatar';
import AddTeamMemberForm from '../../components/teams/AddTeamMemberForm';
import WorkMatchList from '../../components/schedule/WorkMatchList';
import { FiUser, FiUsers, FiMinusCircle, FiCalendar, FiClock } from 'react-icons/fi';
import ListWithActions from '../../components/common/ListWithActions';
import WorkMatchPairForActor from '@/components/schedule/WorkMatchPairForActor';
import AvailabilityList from '../../components/availability/AvailabilityList';
import AvailabilityPolicyForm from '../../components/forms/AvailabilityPolicyForm';
import WatchButton from '@/components/common/WatchButton';
import { useWorkspace } from '../../context/WorkspaceContext';

const TeamDetailPage: React.FC<{ overrideTeamId?: string }> = ({ overrideTeamId }) => {
  const { teamId: paramTeamId } = useParams<{ teamId: string }>();
  const teamId = overrideTeamId || paramTeamId;
  const { team, loading, error } = useTeam(teamId);
  const { applyCommand, workspaceId, database } = useDatabase();
  const { buildUrl } = useWorkspace();
  
  // Function to update the team name
  const updateTeamName = async (newName: string) => {
    if (!team || !workspaceId) return;
    
    try {
      // Create command with the correct type structure
      const command: wasm.DatabaseCommand = {
        type: "UpdateTeam",
        id: team.id,
        actions: [{
          type: "SetName",
          name: newName,
        }],
      };
      
      await applyCommand(command);
    } catch (error) {
      console.error('Error updating team name:', error);
      throw error; // Let the EditableTitle component handle the error
    }
  };

  // Remove a person from the team
  const handleRemovePerson = async (personId: string) => {
    if (!team) return;
    try {
      const command: wasm.DatabaseCommand = {
        type: 'SetTeamMembership',
        membership: {
          member: { Person: personId },
          team: team.id,
        },
        enabled: false,
      };
      await applyCommand(command);
    } catch (error) {
      console.error('Error removing team member:', error);
    }
  };

  // Remove a subteam from the team
  const handleRemoveSubteam = async (subteamId: string) => {
    if (!team) return;
    try {
      const command: wasm.DatabaseCommand = {
        type: 'SetTeamMembership',
        membership: {
          member: { Team: subteamId },
          team: team.id,
        },
        enabled: false,
      };
      await applyCommand(command);
    } catch (error) {
      console.error('Error removing subteam:', error);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center h-full p-6">
        <Spinner size="xl" />
      </div>
    );
  }

  // Error state
  if (error || !team) {
    return (
      <div className="p-6">
        <div className="text-red-500 mb-4">
          {error || 'Team not found'}
        </div>
      </div>
    );
  }
  
  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6 gap-3">
        <EditableTitle
          title={team.name}
          onTitleChange={updateTeamName}
          size="lg"
          className="flex-grow"
        />
        {teamId && (
          <WatchButton
            workspaceId={workspaceId}
            target={{ type: 'team', id: teamId }}
          />
        )}
      </div>

      {/* Team Members */}
      <Card className="mb-6">
        <div className="prose dark:prose-invert max-w-none">
          <h3 className="text-lg font-medium mb-4">Team Members</h3>
          {(() => {
            const peopleRows = (team.memberIds || []).map((personId) => {
              const person = database ? database.people()[personId] : undefined;
              const name = person ? person.name() : personId;
              const avatarUrl = person ? person.avatar_url() : undefined;
              return { kind: 'person' as const, id: personId, name, avatarUrl };
            });

            const teamRows = (team.subTeamIds || []).map((subId) => {
              const sub = database ? database.teams()[subId] : undefined;
              const name = sub ? sub.name() : subId;
              return { kind: 'team' as const, id: subId, name };
            });

            const combined = [...peopleRows, ...teamRows].sort((a, b) => a.name.localeCompare(b.name));
            if (combined.length === 0) return <p className="text-cm-text-muted">No team members</p>;

            const items = combined.map((row) => ({
              key: `${row.kind}-${row.id}`,
              left: (
                row.kind === 'person' ? (
                  <Link to={buildUrl({ kind: 'person', personId: row.id })} className="flex items-center gap-3 group">
                    <Avatar imageUrl={row.avatarUrl} name={row.name} size="sm" />
                    <span className="text-sm text-cm-text-primary group-hover:underline flex items-center gap-2">
                      <FiUser className="text-cm-text-muted" /> {row.name}
                    </span>
                  </Link>
                ) : (
                  <Link to={buildUrl({ kind: 'team', teamId: row.id })} className="flex items-center gap-3 group">
                    <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-600">
                      <FiUsers />
                    </div>
                    <span className="text-sm text-cm-text-primary group-hover:underline flex items-center gap-2">
                      <FiUsers className="text-cm-text-muted" /> {row.name}
                    </span>
                  </Link>
                )
              ),
              right: (
                <button
                  className="text-red-500 hover:text-red-600 p-1 rounded hover:bg-cm-bg-hover"
                  aria-label={row.kind === 'person' ? 'Remove member' : 'Remove subteam'}
                  onClick={() => {
                    if (row.kind === 'person') {
                      if (confirm('Remove this person from the team?')) handleRemovePerson(row.id);
                    } else {
                      if (confirm('Remove this subteam from the team?')) handleRemoveSubteam(row.id);
                    }
                  }}
                >
                  <FiMinusCircle className="w-4 h-4" />
                </button>
              )
            }));

            return <ListWithActions items={items} />;
            
          })()}

          {/* Add member form */}
          <div className="mt-4">
            <AddTeamMemberForm
              teamId={team.id}
              onSubmit={applyCommand}
              onAdded={() => { /* no-op: list will refresh via subscription */ }}
            />
          </div>
        </div>
      </Card>


      {/* Availability */}
      <Card className="mb-6">
        <div className="flex items-center gap-2 mb-4">
          <FiCalendar className="w-5 h-5 text-cm-accent-blue" />
          <h3 className="text-lg font-mono font-medium text-cm-text-primary">Availability</h3>
        </div>
        <p className="text-xs text-cm-text-muted mb-3">
          Team availability policies sit between individual and workspace defaults. First matching policy for a given day wins.
        </p>
        {(() => {
          const wrapped = database ? database.teams()[team.id] : undefined;
          const avs = wrapped ? wrapped.availability() : [];
          const items = avs
            .filter((a: wasm.PersonAvailability) => !!a.id)
            .map((a: wasm.PersonAvailability) => ({ id: a.id as string, policy: a.policy, after: a.after, before: a.before }));

          const onDelete = async (id: string) => {
            const command: wasm.DatabaseCommand = {
              type: 'UpdateTeam',
              id: team.id,
              actions: [
                { type: 'UpdateAvailability', action: { Delete: { id } } },
              ],
            };
            await applyCommand(command);
          };

          const onReorder = async ({ id, before }: { id: string; before: string | null }) => {
            const command: wasm.DatabaseCommand = {
              type: 'UpdateTeam',
              id: team.id,
              actions: [
                { type: 'UpdateAvailability', action: { Reorder: { id, before } } },
              ],
            };
            await applyCommand(command);
          };

          return (
            <div className="mb-4">
              <AvailabilityList items={items} onDelete={onDelete} onReorder={onReorder} emptyText="No team availability policies yet. Workspace defaults apply." />
            </div>
          );
        })()}
        <AvailabilityPolicyForm
          onSubmit={async (availability) => {
            const command: wasm.DatabaseCommand = {
              type: 'UpdateTeam',
              id: team.id,
              actions: [
                { type: 'UpdateAvailability', action: { Upsert: { availability } } },
              ],
            };
            await applyCommand(command);
          }}
        />
      </Card>

      {/* Planning Rhythm */}
      <Card className="mb-6">
        <div className="flex items-center gap-2 mb-4">
          <FiClock className="w-5 h-5 text-cm-accent-purple" />
          <h3 className="text-lg font-mono font-medium text-cm-text-primary">Planning Rhythm</h3>
        </div>
        <p className="text-xs text-cm-text-muted mb-3">
          How long each planning period is. This controls the visual grouping in the team schedule and the default window when scheduling work.
        </p>
        <PlanningRhythmEditor teamId={team.id} />
      </Card>

      {/* Work Matching */}
      <Card className="mb-6">
        <div className="prose dark:prose-invert max-w-none">
          <h3 className="text-lg font-medium mb-4">Work Matching</h3>
        </div>
        <div className="p-2">
          <WorkMatchList type="actor" actor={{ kind: 'Team', id: team.id }} />
          <WorkMatchPairForActor
            actors={[
              { kind: 'Team', id: team.id },
              ...((team.memberIds || []).map((id) => ({ kind: 'Person' as const, id })))
            ]}
          />
        </div>
      </Card>
    </div>
  );
};

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

function PlanningRhythmEditor({ teamId }: { teamId: string }) {
  const { database, applyCommand } = useDatabase();

  const current = useMemo(() => {
    if (!database) return null;
    const wrapped = database.teams()[teamId];
    if (!wrapped) return null;
    const rhythm = wrapped.planning_rhythm();
    if (!rhythm || !rhythm.interval_days) return null;
    return rhythm as { interval_days: number; start_day: string };
  }, [database, teamId]);

  const [intervalDays, setIntervalDays] = useState(current?.interval_days ?? 7);
  const [startDay, setStartDay] = useState(current?.start_day ?? 'Mon');
  const [dirty, setDirty] = useState(false);

  const presets = [
    { label: 'Daily', days: 1 },
    { label: 'Weekly', days: 7 },
    { label: 'Biweekly', days: 14 },
    { label: '4-week', days: 28 },
  ];

  const currentPreset = presets.find(p => p.days === intervalDays);

  const save = async () => {
    const command: wasm.DatabaseCommand = {
      type: 'UpdateTeam',
      id: teamId,
      actions: [{
        type: 'SetPlanningRhythm',
        rhythm: { interval_days: intervalDays, start_day: startDay },
      }],
    };
    await applyCommand(command);
    setDirty(false);
  };

  const clear = async () => {
    const command: wasm.DatabaseCommand = {
      type: 'UpdateTeam',
      id: teamId,
      actions: [{
        type: 'SetPlanningRhythm',
        rhythm: null,
      }],
    };
    await applyCommand(command);
    setDirty(false);
  };

  return (
    <div className="flex items-center gap-3 flex-wrap">
      <div className="flex items-center gap-1.5">
        {presets.map(p => (
          <button
            key={p.days}
            onClick={() => { setIntervalDays(p.days); setDirty(true); }}
            className={`px-2.5 py-1 rounded text-xs transition-colors ${
              intervalDays === p.days
                ? 'bg-cm-accent-purple/20 text-cm-accent-purple border border-cm-accent-purple/40'
                : 'bg-cm-bg-deep text-cm-text-muted border border-cm-border-subtle hover:border-cm-border-mid'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {!currentPreset && (
        <span className="text-xs text-cm-text-secondary font-mono">{intervalDays}d</span>
      )}

      <div className="flex items-center gap-1.5">
        <span className="text-[10px] text-cm-text-muted">starts</span>
        <select
          value={startDay}
          onChange={(e) => { setStartDay(e.target.value); setDirty(true); }}
          className="text-xs bg-cm-bg-deep border border-cm-border-subtle rounded px-2 py-1 text-cm-text-secondary"
        >
          {WEEKDAYS.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
      </div>

      {dirty && (
        <Button size="xs" onClick={save}>Save</Button>
      )}

      {current && !dirty && (
        <button
          onClick={clear}
          className="text-[10px] text-cm-text-muted hover:text-red-400 transition-colors"
        >
          clear (use default)
        </button>
      )}

      {!current && !dirty && (
        <span className="text-[10px] text-cm-text-muted italic">Using workspace default (weekly, Mon)</span>
      )}
    </div>
  );
}

export default TeamDetailPage;
